<template>
    <div class="date-range-picker-inline">
        <div class="btnGroup">
            <SupsisButton
        :classNames="mode === 'day-specific' ? 'btnGroupItem selected' : 'btnGroupItem '"
        :primary="mode === 'day-specific'"
        :ghost="mode !== 'day-specific'"
        @click="() => changeMode('day-specific')"
        small
    >
        {{ $t('message.dayRange') }}
    </SupsisButton>
    <SupsisButton
        :classNames="mode === 'time-specific' ? 'btnGroupItem selected' : 'btnGroupItem '"
        :primary="mode === 'time-specific'"
        :ghost="mode !== 'time-specific'"
        @click="() => changeMode('time-specific')"
        small
    >
        {{ $t('message.hourRange') }}
    </SupsisButton>
    <SupsisButton
        :classNames="mode === 'last24h' ? 'btnGroupItem selected' : 'btnGroupItem '"
        :primary="mode === 'last24h'"
        :ghost="mode !== 'last24h'"
        @click="() => changeMode('last24h')"
        small
    >
        {{ $t('message.last24Hours') }}
    </SupsisButton>
        </div>
        <VueCtkDateTimePicker
            v-model="model"
            :min-date="minDate"
            :max-date="maxDate"
            :label="$t('message.date')"
            range
            auto-close
            inline
            :dark="isDarkMode"
            input-size="sm"
            no-button-now
            :custom-shortcuts="shortcuts"
            output-format="YYYY-MM-DDTHH:mm:ss.sssZ"
            color="#22272d"
            button-color="#22272d"
            id="date-range-picker-1"
            v-if="mode === 'day-specific'"
        />

        <div class="time-specific" v-else>
            <VueCtkDateTimePicker
                v-model="model.startDate"
                :min-date="minDate"
                :max-date="maxDate"
                :label="$t('message.startDateTime')"
                :dark="isDarkMode"
                input-size="sm"
                no-button-now
                output-format="YYYY-MM-DDTHH:mm:ss.sssZ"
                color="#22272d"
                button-color="#22272d"
                id="start-date"
                v-if="mode === 'time-specific'"
            />

            <VueCtkDateTimePicker
                v-model="model.endDate"
                :min-date="minDate"
                :max-date="maxDate"
                :label="$t('message.endDateTime')"
                :dark="isDarkMode"
                input-size="sm"
                output-format="YYYY-MM-DDTHH:mm:ss.sssZ"
                color="#22272d"
                button-color="#22272d"
                id="end-date"
                v-if="mode === 'time-specific'"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import _ from "lodash";
import Avatar from "vue-avatar";
import log2 from "../../utils/log2";
import moment from "moment";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";

const log = log2("DateRangerPickerInline");
export default Vue.extend({
    name: "DateRangerPickerInline",
    components: {
        SupsisButton,
        Avatar,
    },
    props: {
        value: {
            type: Object,
            default: () => ({
                timeSelect: "today",
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        const clone = _.cloneDeep(this.value || {});
        return {
            mode:
                clone.timeSelect === "custom"
                    ? "time-specific"
                    : clone.timeSelect === "last24h"
                    ? "last24h"
                    : "day-specific",
            searchText: "",
            minDate: moment().subtract(1, "year").toISOString(),
            maxDate: moment().endOf("day").toISOString(),
            model: {
                end: clone.endDate,
                start: clone.startDate,
                shortcut: clone.timeSelect,
            },
            shortcuts: [
                { key: "today", label: this.$t('message.today'), value: "day" },
                { key: "yesterday", label: this.$t('message.yesterday'), value: "-day" },
                { key: "thisWeek", label: this.$t('message.thisWeek'), value: "isoWeek" },
                { key: "lastWeek", label: this.$t('message.lastWeek'), value: "-isoWeek" },
                { key: "last7Days", label: this.$t('message.last7Days'), value: 7 },
                { key: "last30Days", label: this.$t('message.last30Days'), value: 30 },
                { key: "thisMonth", label: this.$t('message.thisMonth'), value: "month" },
                { key: "lastMonth", label: this.$t('message.lastMonth'), value: "-month" },
                ..._.range(2, 13).map((i) => ({
                    key: `month${i}`,
                    label: moment().subtract(i, "month").format("MMMM YYYY"),
                    value: () => {
                        return {
                            start: moment().subtract(i, "month").startOf("month"),
                            end: moment().subtract(i, "month").endOf("month"),
                        };
                    },
                })),
            ],
        };
    },
    computed: {
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
        }),
    },

    methods: {
        changeMode(mode) {
            this.mode = mode;
            if (mode === "last24h") {
                this.model = {
                    start: moment().subtract(1, "day").toISOString(),
                    end: undefined,
                    shortcut: "last24h",
                };
            }
        },
    },
    watch: {
        value: {
            deep: true,
            handler(val) {
                if (_.isEqual(this.clone, val)) return;
                this.clone = _.cloneDeep(val);
            },
        },
        model: {
            deep: true,
            handler(val) {
                const result = {
                    timeSelect:
                        this.mode === "day-specific" ? val.shortcut : this.mode === "last24h" ? "last24h" : "custom",
                    startDate: val.start || val.startDate,
                    endDate: this.mode === "last24h" ? undefined : val.end || val.endDate,
                };
                this.$emit("input", _.cloneDeep(result));
            },
        },
    },
});
</script>

<style scoped lang="scss">
.date-range-picker-inline {
    display: flex;
    flex-direction: column;
    gap: 3px;
    width: 100%;

    .btnGroup {
        display: flex;
        border: 1px solid var(--color-border-default);
        border-radius: 6px;
        background: var(--color-bg-neutral-default);

        .btnGrupItem {
            &.selected {
                background: var(--color-bg-oncomponent-default);
                box-shadow: var(--shadow-interaction-200);
            }
        }

        .supsis-button {
            border: none !important;
            font-size: 13px;
            height: 25px;
        }
    }

    .time-specific {
        display: flex;
        flex-direction: column;
        gap: 3px;
        width: 100%;

        .date-time-picker {
            width: 100%;
        }
    }
}
</style>
